/**
 * useClientConfig
 *
 * Hook para carregar configurações do tenant via API /core/config/client.
 * Cacheia em zustand store. Substitui referências a import.meta.env por config dinâmico.
 *
 * @since 2026-04-17
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/apiClient';

// =============================================================================
// Types
// =============================================================================

export interface ClientConfig {
  clientCode: string;
  clientName: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  features: Record<string, boolean>;
  settings: Record<string, unknown>;
  source: 'hub' | 'env';
}

// =============================================================================
// Store (persisted)
// =============================================================================

interface ClientConfigState {
  config: ClientConfig | null;
  isLoaded: boolean;
  setConfig: (config: ClientConfig) => void;
  clear: () => void;
}

export const useClientConfigStore = create<ClientConfigState>()(
  persist(
    (set) => ({
      config: null,
      isLoaded: false,
      setConfig: (config) => set({ config, isLoaded: true }),
      clear: () => set({ config: null, isLoaded: false }),
    }),
    { name: 'tmf-client-config' },
  ),
);

// =============================================================================
// API
// =============================================================================

async function fetchClientConfig(): Promise<ClientConfig> {
  return api.get('/core/config/client');
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook para acessar configurações do tenant.
 *
 * Usage:
 * ```tsx
 * const { config, isLoading } = useClientConfig();
 * const logo = config?.logoUrl;
 * const hasFeature = config?.features?.kanban ?? false;
 * ```
 */
export function useClientConfig(options?: { enabled?: boolean }) {
  const { config: cached, setConfig } = useClientConfigStore();
  const enabled = options?.enabled ?? true;

  const query = useQuery({
    queryKey: ['client-config'],
    queryFn: async () => {
      const data = await fetchClientConfig();
      setConfig(data);
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (matches backend Redis TTL)
    initialData: cached || undefined,
    enabled,
  });

  return {
    config: query.data || cached,
    isLoading: query.isLoading && !cached,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Helper para verificar feature flag do tenant.
 *
 * Usage:
 * ```tsx
 * const hasKanban = useFeatureFlag('kanban');
 * ```
 */
export function useFeatureFlag(flag: string): boolean {
  const { config } = useClientConfig();
  return config?.features?.[flag] ?? false;
}

/**
 * Helper para acessar setting do tenant.
 *
 * Usage:
 * ```tsx
 * const maxUpload = useClientSetting<number>('max_upload_mb', 10);
 * ```
 */
export function useClientSetting<T>(key: string, defaultValue: T): T {
  const { config } = useClientConfig();
  return (config?.settings?.[key] as T) ?? defaultValue;
}
